import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EvaluationStepCard from '../../composites/EvaluationStepCard';

// Criteria items are keyed by attribute; the card reports row ids, which are those attributes.
const updateItem = (criteria, attribute, patch) => ({
    ...criteria,
    items: criteria.items.map((item) => (item.attribute === attribute ? { ...item, ...patch } : item)),
});

const StepEvaluationCriteria = ({
    criteria,
    steps,
    onChange,
    attributesOptions = [],
    onAddSkill,
    updateCriteria,
    onRemoveCriteria,
    onDuplicate,
    canDuplicate = false,
    isDisabled = false,
    ratingOptions = [],
    isAttributeEvaluated = () => false,
    getAttributeTitle = () => '',
}) => {
    const { step_id, items = [] } = criteria;

    const rows = useMemo(
        () => items.map((item) => ({
            id: item.attribute,
            title: getAttributeTitle(item.attribute),
            question: item.question,
            scoreType: item.score_type,
            disabled: typeof isAttributeEvaluated === 'function' && isAttributeEvaluated(step_id, item.attribute),
        })),
        [items, step_id, getAttributeTitle, isAttributeEvaluated],
    );

    const skillOptions = useMemo(() => {
        const selected = new Set(items.map(({ attribute }) => attribute));
        return attributesOptions.filter(({ id }) => !selected.has(id));
    }, [items, attributesOptions]);

    return (
        <EvaluationStepCard
            id={`step_${step_id}`}
            stepId={step_id}
            stepOptions={steps}
            onStepChange={onChange}
            stepDisabled={isDisabled}
            rows={rows}
            ratingOptions={ratingOptions}
            onQuestionChange={(attribute, question) => updateCriteria(updateItem(criteria, attribute, { question }))}
            onScoreTypeChange={(attribute, score_type) => updateCriteria(updateItem(criteria, attribute, { score_type }))}
            onRowRemove={(attribute) => updateCriteria({ ...criteria, items: items.filter((item) => item.attribute !== attribute) })}
            skillOptions={skillOptions}
            onSkillAdd={onAddSkill}
            onDuplicate={canDuplicate ? onDuplicate : undefined}
            onRemove={onRemoveCriteria}
            removeDisabled={isDisabled}
        />
    );
};

StepEvaluationCriteria.propTypes = {
    criteria: PropTypes.shape({
        step_id: PropTypes.string.isRequired,
        items: PropTypes.arrayOf(
            PropTypes.shape({
                attribute: PropTypes.string.isRequired,
                question: PropTypes.string,
                score_type: PropTypes.string,
            }),
        ).isRequired,
    }).isRequired,
    steps: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChange: PropTypes.func.isRequired,
    attributesOptions: PropTypes.arrayOf(PropTypes.object),
    onAddSkill: PropTypes.func.isRequired,
    updateCriteria: PropTypes.func.isRequired,
    onRemoveCriteria: PropTypes.func.isRequired,
    onDuplicate: PropTypes.func,
    canDuplicate: PropTypes.bool,
    isDisabled: PropTypes.bool,
    ratingOptions: PropTypes.array,
    isAttributeEvaluated: PropTypes.func,
    getAttributeTitle: PropTypes.func,
};

export default StepEvaluationCriteria;
